import { useState } from 'react'
import { useLogin } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'

const MAX_LOGIN_ATTEMPTS = 5
const LOCKOUT_DURATION_MS = 5 * 60 * 1000 // 5 minutes
const LS_ATTEMPTS_KEY = 'uyari_login_attempts'
const LS_LOCKOUT_KEY = 'uyari_login_lockout'

function getLoginAttempts(): number {
  return parseInt(localStorage.getItem(LS_ATTEMPTS_KEY) || '0', 10)
}

function setLoginAttempts(n: number) {
  localStorage.setItem(LS_ATTEMPTS_KEY, String(n))
}

function getLockoutUntil(): number | null {
  const v = localStorage.getItem(LS_LOCKOUT_KEY)
  return v ? parseInt(v, 10) : null
}

function setLockoutUntil(ts: number | null) {
  if (ts === null) {
    localStorage.removeItem(LS_LOCKOUT_KEY)
  } else {
    localStorage.setItem(LS_LOCKOUT_KEY, String(ts))
  }
}

function clearLockout() {
  setLoginAttempts(0)
  setLockoutUntil(null)
}

export const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const login = useLogin()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const now = Date.now()
    const lockout = getLockoutUntil()

    if (lockout && now < lockout) {
      const remainingSeconds = Math.ceil((lockout - now) / 1000)
      toast.error(`Demasiados intentos. Espera ${remainingSeconds} segundos.`)
      return
    }

    if (lockout && now >= lockout) {
      clearLockout()
    }

    const attempts = getLoginAttempts() + 1
    setLoginAttempts(attempts)

    if (attempts > MAX_LOGIN_ATTEMPTS) {
      setLockoutUntil(now + LOCKOUT_DURATION_MS)
      toast.error('Demasiados intentos fallidos. Cuenta bloqueada por 5 minutos.')
      return
    }

    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          setPassword('')
          clearLockout()
        },
      }
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Uyari Admin</CardTitle>
          <CardDescription>Panel de Administracion</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@uyari.io"
                required
                maxLength={254}
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contrasena</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                maxLength={128}
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full" isLoading={login.isPending}>
              Iniciar Sesion
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
